"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { isIOS, isAndroidDevice } from "@/lib/utils/desktop-detection";
import { useTranslations } from "@/hooks/use-translations";
import { useSiteContent } from "@/components/marketing/use-site-content";

const IOS_APP_STORE_URL =
  process.env.NEXT_PUBLIC_IOS_APP_STORE_URL ||
  "https://apps.apple.com/app/growthsync/id6756941263";
const ANDROID_PLAY_STORE_URL =
  process.env.NEXT_PUBLIC_ANDROID_PLAY_STORE_URL ||
  "https://play.google.com/store/apps/details?id=com.growthsync.network";

/* The two store marks, drawn rather than emoji. Until 2026-08-24 these buttons carried a 📱 and a
   🤖, which render as a pager and a cartoon face on macOS: the page looked like a placeholder. */
function AppleMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
      <path d="M16.365 1.43c0 1.14-.42 2.2-1.25 3.05-.99 1.02-2.19 1.61-3.32 1.52a3.4 3.4 0 0 1-.03-.4c0-1.1.49-2.25 1.3-3.06.42-.44.96-.8 1.62-1.09.65-.28 1.27-.44 1.85-.47.01.15.03.3.03.45ZM20.5 17.1c-.34.79-.5 1.14-.94 1.83-.61.97-1.48 2.17-2.55 2.18-.95.01-1.2-.62-2.49-.61-1.29 0-1.56.62-2.51.62-1.07.01-1.89-1.08-2.5-2.04-1.71-2.7-1.89-5.86-.84-7.55.75-1.2 1.94-1.9 3.06-1.9 1.13 0 1.85.62 2.78.62.91 0 1.46-.62 2.77-.62 1 0 2.05.54 2.8 1.48-2.46 1.35-2.06 4.87.42 5.99Z" />
    </svg>
  );
}

function PlayMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
      <path d="M3.6 2.3c-.25.26-.4.67-.4 1.2v17c0 .53.15.94.4 1.2l.06.05L13.2 12v-.02L3.66 2.25l-.06.05Zm11 8.7L4.9 1.3l10.9 6.26L14.6 11Zm0 2 1.2 1.44L4.9 22.7l9.7-9.7Zm2.6-3.13 2.83 1.62c.81.46.81 1.22 0 1.69l-2.84 1.63L15.7 12l1.5-2.13Z" />
    </svg>
  );
}

export default function GetAppPage() {
  const { t } = useTranslations("getApp");
  const site = useSiteContent();
  // "{site}" is left in the string on purpose so each language can put the link where its own
  // word order wants it; the text either side of it is rendered around a real anchor.
  const [browserBefore, browserAfter = ""] = t("page.browser").split("{site}");
  const [deviceType, setDeviceType] = useState<"ios" | "android" | "desktop" | null>(null);

  useEffect(() => {
    if (isIOS()) {
      setDeviceType("ios");
      window.location.href = IOS_APP_STORE_URL;
    } else if (isAndroidDevice()) {
      setDeviceType("android");
      window.location.href = ANDROID_PLAY_STORE_URL;
    } else {
      setDeviceType("desktop");
    }
  }, []);

  /* On a phone this page exists only to bounce to the right store, so it shows a held breath
     rather than a page. On the way there it should still look like GrowthSync.

     Only once we KNOW it is a phone. The check used to be `!== "desktop"`, which is also true
     before the effect has run, so the server rendered the spinner for everybody: a desktop
     visitor saw a loading state flash before the page, and Google indexed a page whose only
     words were "Taking you to the app store". */
  if (deviceType === "ios" || deviceType === "android") {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6">
        <div className="text-center">
          <div className="mx-auto mb-5 h-10 w-10 animate-spin rounded-full border-2 border-neutral-200 border-t-primary" />
          <p className="text-[15px] text-neutral-500">{t("page.redirecting")}</p>
        </div>
      </div>
    );
  }

  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[460px]"
        style={{
          background:
            "radial-gradient(60% 100% at 30% 0%, rgba(33,122,246,0.10) 0%, rgba(33,122,246,0.04) 45%, rgba(250,250,250,0) 78%)",
        }}
      />
      <div className="gs-page relative py-14 md:py-20">
        <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-16">
          <div className="max-w-[36rem]">
            <p className="gs-eyebrow text-primary">{t("page.eyebrow")}</p>
            <h1 className="gs-headline mt-3 text-balance text-neutral-900">
              {t("page.heading")}
            </h1>
            <p className="gs-lede mt-5 text-pretty text-neutral-600">
              {t("page.lede")}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={IOS_APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-14 items-center justify-center gap-3 rounded-2xl bg-neutral-900 px-7 text-[15px] font-bold text-white transition-all hover:scale-[1.02] hover:bg-neutral-800"
              >
                <AppleMark />
                {site.footer.appStore}
              </a>
              <a
                href={ANDROID_PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-14 items-center justify-center gap-3 rounded-2xl border border-neutral-200 bg-white px-7 text-[15px] font-bold text-neutral-900 transition-all hover:scale-[1.02] hover:border-primary/40"
              >
                <PlayMark />
                {site.footer.googlePlay}
              </a>
            </div>

            <p className="mt-6 text-[14px] leading-relaxed text-neutral-500">
              {browserBefore}
              <a href="https://growth-sync.com" className="font-semibold text-primary underline underline-offset-[3px]">
                growth-sync.com
              </a>
              {browserAfter}
            </p>
          </div>

          {/* The old copy told desktop visitors to "scan the QR code from the desktop app", and no
              QR code was ever drawn. Here is one, pointing at this page. */}
          <div className="rounded-[1.75rem] border border-neutral-200 bg-white p-7 text-center shadow-[0_20px_60px_-30px_rgba(11,99,224,0.45)] lg:p-8">
            <Image
              src="/qr-get-app.svg"
              alt={t("qrAlt")}
              width={188}
              height={188}
              className="mx-auto h-[188px] w-[188px]"
              unoptimized
            />
            <p className="mt-5 max-w-[15rem] text-[13.5px] leading-relaxed text-neutral-500">
              {t("page.qrHint")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
